import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { AppConfig } from '../../../app.settings';
import { ConnectToRemoteMachine } from '../../models/remote-control/connect-to-remote-machine.model';
import { MouseClickHoldMovementInput } from '../../models/remote-control/mouse-click-hold-movement.model';
import { RemoteClickEvent } from '../../models/remote-control/remote-click-event.model';
import { RemoteCursorPointEvent } from '../../models/remote-control/remote-cursor-point-event.model';
import { MonitorKey } from '../../models/view-models/monitor-models/monitor-key.type';
import { UIService } from '../utils/ui.service';

@Injectable({
  providedIn: 'root'
})
export class RemoteControlService {

  public remoteControlMonitorKey: BehaviorSubject<MonitorKey> = new BehaviorSubject(null);
  public isRemoteControlAllowed: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private endpoint: string;

  constructor(
    private readonly http: HttpClient,
    private readonly config: AppConfig,
    private readonly uiService: UIService
  ) {
    this.endpoint = this.config.getConfig('api') + 'RemoteControl';
  }

  connectToMachine(data: ConnectToRemoteMachine): Observable<void> {
    return this.http.post<void>(`${this.endpoint}/connect-to-machine`, data);
  }

  setCursorPosition(data: RemoteCursorPointEvent, monitorHandler: string): Observable<void> {
    const params: HttpParams = new HttpParams()
      .set('monitorHandler', monitorHandler);

    return this.http.post<void>(`${this.endpoint}/set-cursor-position`, data, { params });
  }

  clickRemoteDesktop(data: RemoteClickEvent): Observable<void> {
    return this.http.post<void>(`${this.endpoint}/click-remote-desktop`, data);
  }

  scrollRemoteDesktopVertically(scrollAmount: number): Observable<void> {
    const params: HttpParams = new HttpParams()
      .set('scrollAmount', scrollAmount);

    return this.http.post<void>(`${this.endpoint}/scroll-remote-desktop`, null, { params });
  }

  scrollRemoteDesktopHorizontally(scrollAmount: number): Observable<void> {
    const params: HttpParams = new HttpParams()
      .set('scrollAmount', scrollAmount);

    return this.http.post<void>(`${this.endpoint}/scroll-remote-desktop-horizontal`, null, { params });
  }

  manageKeyboard(keyCode: number, shortcutKey: number): Observable<void> {
    const params: HttpParams = new HttpParams()
      .set('keyCode', keyCode.toString())
      .set('shortcutKey', shortcutKey.toString());

    return this.http.post<void>(`${this.endpoint}/manage-keyboard`, null, { params });
  }

  disconnectRemoteDesktop(monitorHandler: string): Observable<void> {
    const params: HttpParams = new HttpParams()
      .set('monitorHandler', monitorHandler);

    return this.http.post<void>(`${this.endpoint}/disconnect-remote-desktop`, null, { params });
  }

  mouseHoldMovement(data: MouseClickHoldMovementInput): Observable<void> {
    return this.http.post<void>(`${this.endpoint}/mouse-hold-movement`, data);
  }

  public closeRemoteControlSession(): Observable<void> {
    this.uiService.isBottomPanelButtonHidden = false;
    this.uiService.hideBottomPanel();
    return this.disconnectRemoteControl();
  }

  private disconnectRemoteControl(): Observable<void> {
    if (this.remoteControlMonitorKey.value) {
      const obs$: Observable<void> = this.disconnectRemoteDesktop(this.remoteControlMonitorKey.value.MonitorHandler).pipe(
        map((mappedData) => {
          this.remoteControlMonitorKey.next(null);
          return mappedData;
        })
      );

      return obs$;
    }

    return new BehaviorSubject(null).asObservable();
  }
}
